import { action, computed, observable } from 'mobx'
import { validationContext } from 'validx'
import {
  type ErrorDescriptor,
  receiveErrors,
} from '@/features/legends/editor/analyzer-errors'
import { ConfigEditorViewModelBase } from './ConfigEditorViewModelBase'
import type { CustomerRatingMethod, RatingConfig } from './legend-options'

/**
 * A rating method, or none: a document can have a rating config without one.
 */
export type CustomerRatingMethodChoice = CustomerRatingMethod | ''

/**
 * What clients can rate once the job is done, and how.
 */
export class RatingConfigEditorViewModel extends ConfigEditorViewModelBase<RatingConfig> {
  /**
   * The rating methods to pick from, with their labels.
   */
  readonly ratingMethods: [CustomerRatingMethodChoice, string][] = [
    ['Stars', 'Stars'],
    ['Thumbs', 'Thumbs up / Thumbs down'],
  ]

  @observable accessor proRatingEnabled = false
  @observable accessor platformRatingEnabled = false
  /**
   * Empty when the document has a rating config without a method, which is
   * then saved without one.
   */
  @observable accessor customerRatingMethod: CustomerRatingMethodChoice =
    'Thumbs'

  readonly validation = validationContext(this, {})

  /**
   * Whether clients rate anything, which is when the rating method matters.
   */
  @computed
  get hasRatings() {
    return this.proRatingEnabled || this.platformRatingEnabled
  }

  /**
   * Lets clients rate the pro, or not.
   */
  @action.bound
  setProRatingEnabled(value: boolean) {
    this.proRatingEnabled = value
  }

  /**
   * Lets clients rate the platform, or not.
   */
  @action.bound
  setPlatformRatingEnabled(value: boolean) {
    this.platformRatingEnabled = value
  }

  /**
   * Keeps the current ratings when the document has no rating config.
   */
  @action
  hydrate(config: RatingConfig | undefined) {
    if (!config) {
      return
    }
    this.proRatingEnabled = !!config.proRatingEnabled
    this.platformRatingEnabled = !!config.platformRatingEnabled
    this.customerRatingMethod = config.customerRatingMethod ?? ''
  }

  /** @inheritDoc */
  receiveErrors(descriptor: ErrorDescriptor) {
    const { categories: _, ...rest } = descriptor.inner ?? {}
    receiveErrors(this, rest)
  }

  /** @inheritDoc */
  toJSON(): RatingConfig {
    return {
      proRatingEnabled: this.proRatingEnabled,
      platformRatingEnabled: this.platformRatingEnabled,
      customerRatingMethod: this.customerRatingMethod || undefined,
    }
  }
}
